import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';

const steps = [
  {
    num: '01',
    icon: 'ri-search-eye-line',
    title: 'AI Visibility Audit',
    duration: 'Weeks 1–2',
    desc: 'We run 50+ relevant queries across ChatGPT, Gemini, and Perplexity. We map where you and your competitors appear. You get a full report — your current AI visibility score and exactly what\'s costing you citations.',
    deliverable: 'AI Visibility Report + Competitor Benchmark',
  },
  {
    num: '02',
    icon: 'ri-tools-line',
    title: 'Technical Foundation',
    duration: 'Weeks 2–3',
    desc: 'We fix crawlability issues, implement schema markup, and ensure all AI systems can properly access and understand your website. Many businesses unknowingly block AI crawlers — we fix that first.',
    deliverable: 'Schema Implementation + Crawlability Audit',
  },
  {
    num: '03',
    icon: 'ri-article-line',
    title: 'Content Transformation',
    duration: 'Weeks 3–6',
    desc: 'We restructure existing pages for AI retrieval and create new Q&A content, service explainers, and local authority articles — all built to be cited. Every piece is formatted for maximum AI extractability.',
    deliverable: 'Restructured Pages + New Answer-Ready Content',
  },
  {
    num: '04',
    icon: 'ri-shield-star-line',
    title: 'Authority Building',
    duration: 'Month 2',
    desc: 'We begin the review generation programme, directory audit, and third-party mention strategy — building the external signals that AI systems use to validate your business authority across the web.',
    deliverable: 'Review Strategy + Directory Report + Citation Building',
  },
  {
    num: '05',
    icon: 'ri-checkbox-circle-line',
    title: 'First Results',
    duration: 'Months 2–3',
    desc: 'Most clients see their first AI citations within 6–10 weeks. We document every appearance and build on what\'s working — identifying which content is winning citations and expanding from there.',
    deliverable: 'First AI Citation Report + Expansion Plan',
  },
  {
    num: '06',
    icon: 'ri-bar-chart-2-line',
    title: 'Monitor, Optimise, Report',
    duration: 'Ongoing',
    desc: 'Monthly reporting, continuous content updates, and ongoing competitor monitoring to keep you ahead as AI search evolves. What gets cited today may be outcompeted tomorrow — we stay on it so you don\'t have to.',
    deliverable: 'Monthly AI Visibility Report + Strategy Updates',
  },
];

export default function GeoProcessSection() {
  const groupRef = useScrollRevealGroup();

  return (
    <section id="geo-process" className="relative pt-24 pb-16 bg-ds-bg-2 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-red to-transparent" />
      <div className="absolute inset-0 hero-grid opacity-10 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6" ref={groupRef}>

        {/* Header */}
        <div className="mb-16">
          <span className="section-label reveal">How We Do It</span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <h2 className="font-display text-6xl md:text-7xl text-white leading-none reveal">
              THE DARKSIDE
              <br />
              <span className="text-ds-red neon-text-red">DIGITAL GEO</span>
              <br />
              PROCESS.
            </h2>
            <p className="font-body text-white/50 text-sm leading-relaxed max-w-xs reveal">
              From audit to authority — most clients see their first AI citations within 6–10 weeks. Here&apos;s exactly what that looks like, step by step.
            </p>
          </div>
          <div className="mt-8 h-px w-24 bg-ds-red reveal" style={{ boxShadow: '0 0 12px rgba(180,255,57,0.5)' }} />
        </div>

        {/* Steps table */}
        <div className="space-y-px reveal">
          {steps.map((step) => (
            <div
              key={step.num}
              className="grid grid-cols-12 bg-ds-bg group hover:bg-ds-bg-2 transition-colors duration-300 border border-transparent hover:border-ds-border"
            >
              {/* Number */}
              <div className="col-span-1 flex items-center justify-center py-8 border-r border-ds-border">
                <span className="font-display text-ds-red text-sm tracking-widest">{step.num}</span>
              </div>
              {/* Icon + Title + Duration */}
              <div className="col-span-3 flex items-center gap-4 px-6 py-8 border-r border-ds-border">
                <div className="w-8 h-8 flex items-center justify-center border border-ds-border group-hover:border-ds-red/50 transition-colors duration-300 flex-shrink-0">
                  <i className={`${step.icon} text-white/40 group-hover:text-ds-red text-base transition-colors duration-300`} />
                </div>
                <div>
                  <div className="font-display text-white text-sm tracking-wide leading-tight">{step.title}</div>
                  <div className="font-body text-white/30 text-xs mt-0.5 tracking-wider">{step.duration}</div>
                </div>
              </div>
              {/* Description */}
              <div className="col-span-5 px-6 py-8 flex items-center border-r border-ds-border">
                <p className="font-body text-white/55 text-sm leading-relaxed">{step.desc}</p>
              </div>
              {/* Deliverable */}
              <div className="col-span-3 px-6 py-8 flex items-center">
                <div className="flex items-start gap-2">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-fill text-ds-red text-xs" />
                  </div>
                  <span className="font-body text-white/50 text-xs leading-snug">{step.deliverable}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Agency differentiation strip */}
        <div className="mt-10 border border-ds-border bg-ds-bg p-8 text-center reveal" style={{ borderTop: '2px solid #B4FF39' }}>
          <p className="font-display text-xl md:text-2xl text-white leading-tight max-w-3xl mx-auto">
            Most agencies are still focused entirely on Google. While they optimise for yesterday,{' '}
            <span className="text-ds-red">we&apos;re building your visibility in tomorrow&apos;s search.</span>
          </p>
        </div>

        {/* Timeline stats */}
        <div className="mt-6 flex flex-col md:flex-row gap-6 reveal">
          {[
            { icon: 'ri-time-line', label: '6–10 Weeks', sub: 'To first AI citations' },
            { icon: 'ri-calendar-line', label: '3 Month Minimum', sub: 'Required to see real results' },
            { icon: 'ri-user-line', label: 'Dedicated Strategist', sub: 'A real person managing your GEO' },
          ].map((item) => (
            <div key={item.label} className="flex-1 border border-ds-border bg-ds-bg-2 p-6 flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-ds-red text-xl`} />
              </div>
              <div>
                <div className="font-display text-white text-base tracking-wide">{item.label}</div>
                <div className="font-body text-white/40 text-xs mt-0.5">{item.sub}</div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
